import CircularProgress from '@mui/material/CircularProgress';
import { type SxProps, type Theme } from '@mui/material/styles';
import type { ComponentProps } from 'react';

import type { AnalysisResultVariant } from '../../types';
import { AnalysisResultChip } from '../AnalysisResultChip/AnalysisResultChip';
import { Image as UiImage } from '../Image/Image';
import { ImageOverlay } from '../ImageOverlay/ImageOverlay';
import { ProgressiveImage } from '../ProgressiveImage/ProgressiveImage';

type Props = ComponentProps<typeof ProgressiveImage> & {
  isLoading?: boolean;
  uploadProgress?: number;
  analysisStatus?: AnalysisResultVariant;
  obfuscateAi?: boolean;
  loadingStrategy?: 'progressive' | 'eager';
};

export const EvidenceImage = (props: Props) => {
  const {
    alt,
    isLoading,
    uploadProgress,
    analysisStatus,
    obfuscateAi,
    loadingStrategy = 'progressive',
    placeholderSrc,
    rounded,
    showLoader,
    sx,
    zoomOnHover,
    minHeight,
    ...rest
  } = props;
  const defaultImageSx = {
    aspectRatio: '1 / 1',
    display: 'block',
    height: 'auto',
  } as const;
  const imageAlt = alt ?? 'photo evidence';
  const mergedImageSx: SxProps<Theme> = [defaultImageSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])];
  const resolvedRounded = rounded ?? true;

  return (
    <>
      {isLoading && uploadProgress ? (
        <ImageOverlay lighten>
          <CircularProgress
            sx={{
              color: 'white',
            }}
            variant="determinate"
            value={uploadProgress}
          />
        </ImageOverlay>
      ) : null}
      {!isLoading && analysisStatus ? (
        <ImageOverlay>
          <AnalysisResultChip variant={analysisStatus} obfuscateAi={obfuscateAi} />
        </ImageOverlay>
      ) : null}
      {loadingStrategy === 'eager' ? (
        <UiImage
          {...rest}
          alt={imageAlt}
          data-testid="evidence-image"
          loading="eager"
          rounded={resolvedRounded}
          sx={mergedImageSx}
        />
      ) : (
        <ProgressiveImage
          {...rest}
          alt={imageAlt}
          data-testid="evidence-image"
          minHeight={minHeight}
          placeholderSrc={placeholderSrc}
          rounded={resolvedRounded}
          showLoader={showLoader ?? !uploadProgress}
          sx={mergedImageSx}
          zoomOnHover={zoomOnHover ?? true}
        />
      )}
    </>
  );
};
